import React, { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, CheckCircle, Clock, BookOpen, MapPin, Building2, Map } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

// Location Distribution Bar Component
function LocationBar({ label, count, total, color = "bg-primary" }) {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-stone-600">{label}</span>
        <span className="text-sm font-bold text-foreground">{count} ({percentage}%)</span>
      </div>
      <div className="w-full bg-stone-200 rounded-full h-2">
        <div
          className={`${color} rounded-full h-2 transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// Collapsible Location Section Component
function LocationSection({ title, icon: Icon, data, total, color, emptyMessage }) {
  const [expanded, setExpanded] = useState(false);
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const hasData = entries.length > 0;
  const displayEntries = expanded ? entries : entries.slice(0, 5);
  
  return (
    <div className="border border-stone-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${color}`} />
        <h4 className="font-semibold text-foreground">{title}</h4>
        <span className="text-xs bg-stone-100 px-2 py-1 rounded-full text-stone-600">
          {entries.length} locations
        </span>
      </div>
      {hasData ? (
        <div className="space-y-3">
          {displayEntries.map(([location, count]) => (
            <LocationBar 
              key={location} 
              label={location} 
              count={count} 
              total={total}
              color={color.replace('text-', 'bg-')}
            />
          ))}
          {entries.length > 5 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm text-primary hover:underline mt-2"
            >
              {expanded ? 'Show less' : `Show ${entries.length - 5} more...`}
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-stone-500 italic">{emptyMessage}</p>
      )}
    </div>
  );
}

export default function Reports() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/reports/statistics');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="reports-page">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-foreground mb-2">Reports & Statistics</h1>
            <p className="text-stone-600">Overview of distribution and impact metrics</p>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card className="border-stone-200 shadow-card" data-testid="total-families-stat">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-stone-600">Total Families</CardTitle>
                    <Users className="w-5 h-5 text-primary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-foreground">{stats?.total_families || 0}</div>
                    <p className="text-xs text-stone-500 mt-1">Registered families</p>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 shadow-card" data-testid="delivered-stat">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-stone-600">Delivered</CardTitle>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-foreground">{stats?.delivered || 0}</div>
                    <p className="text-xs text-stone-500 mt-1">
                      {stats?.total_families > 0 ? `${Math.round((stats.delivered / stats.total_families) * 100)}%` : '0%'} completion rate
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 shadow-card" data-testid="pending-stat">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-stone-600">Pending</CardTitle>
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-foreground">{stats?.pending || 0}</div>
                    <p className="text-xs text-stone-500 mt-1">Awaiting delivery</p>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 shadow-card" data-testid="total-children-stat">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-stone-600">Total Children</CardTitle>
                    <BookOpen className="w-5 h-5 text-secondary" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-foreground">{stats?.total_children || 0}</div>
                    <p className="text-xs text-stone-500 mt-1">Children served</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card className="border-stone-200 shadow-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      Geographic Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4" data-testid="location-stats">
                      {stats?.location_distribution && Object.entries(stats.location_distribution).map(([location, count]) => {
                        const percentage = stats.total_families > 0 ? Math.round((count / stats.total_families) * 100) : 0;
                        const colorMap = {
                          'DC Ward': 'bg-blue-500',
                          'Virginia': 'bg-green-500',
                          'Maryland': 'bg-purple-500'
                        };
                        return (
                          <div key={location} className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-stone-600">{location}</span>
                              <span className="text-sm font-bold text-foreground">{count} ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-stone-200 rounded-full h-2">
                              <div
                                className={`${colorMap[location] || 'bg-primary'} rounded-full h-2 transition-all`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-stone-200 shadow-card">
                  <CardHeader>
                    <CardTitle>Impact Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-4 bg-background rounded-lg">
                        <p className="text-sm text-stone-600 mb-1">Families Served</p>
                        <p className="text-2xl font-bold text-primary">{stats?.total_families || 0}</p>
                      </div>
                      <div className="p-4 bg-background rounded-lg">
                        <p className="text-sm text-stone-600 mb-1">Children Reached</p>
                        <p className="text-2xl font-bold text-secondary">{stats?.total_children || 0}</p>
                      </div>
                      <div className="p-4 bg-background rounded-lg">
                        <p className="text-sm text-stone-600 mb-1">Delivery Success Rate</p>
                        <p className="text-2xl font-bold text-green-600">
                          {stats?.total_families > 0 ? `${Math.round((stats.delivered / stats.total_families) * 100)}%` : '0%'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Location Breakdown */}
              <Card className="border-stone-200 shadow-card mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Map className="w-5 h-5 text-primary" />
                    Detailed Location Breakdown
                  </CardTitle>
                  <p className="text-sm text-stone-500 mt-1">
                    Family distribution by DC Ward, Virginia cities/counties, and Maryland cities/counties
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* DC Wards */}
                    <LocationSection
                      title="DC Wards"
                      icon={MapPin}
                      data={stats?.ward_distribution}
                      total={stats?.location_distribution?.['DC Ward'] || 0}
                      color="text-blue-500"
                      emptyMessage="No DC families registered"
                    />
                    
                    {/* Virginia */}
                    <div className="space-y-4">
                      <LocationSection
                        title="Virginia Cities"
                        icon={Building2}
                        data={stats?.virginia_city_distribution}
                        total={stats?.location_distribution?.['Virginia'] || 0}
                        color="text-green-500"
                        emptyMessage="No Virginia families registered"
                      />
                      <LocationSection
                        title="Virginia Counties"
                        icon={Map}
                        data={stats?.virginia_county_distribution}
                        total={stats?.location_distribution?.['Virginia'] || 0}
                        color="text-green-600"
                        emptyMessage="No Virginia families registered"
                      />
                    </div>
                    
                    {/* Maryland */}
                    <div className="space-y-4">
                      <LocationSection
                        title="Maryland Cities"
                        icon={Building2}
                        data={stats?.maryland_city_distribution}
                        total={stats?.location_distribution?.['Maryland'] || 0}
                        color="text-purple-500"
                        emptyMessage="No Maryland families registered"
                      />
                      <LocationSection
                        title="Maryland Counties"
                        icon={Map}
                        data={stats?.maryland_county_distribution}
                        total={stats?.location_distribution?.['Maryland'] || 0}
                        color="text-purple-600"
                        emptyMessage="No Maryland families registered"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}